import { Component, ComponentType, Convention } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Physical, RelativeTo } from './Physical';

const topToBottomComparer = (a: Component, b: Component) => {
    while (true) {
        if (a.parent === b) return 1;
        if (b.parent === a) return -1;
        if (a.parent === b.parent) return a.order - b.order;
        const ad = a.depth();
        const bd = b.depth();
        if (ad > bd || ad === bd) a = a.parent;
        if (bd > ad || bd === ad) b = b.parent;
    }
};

const isInBoundary = (p: Physical) => {
    const pp = p.parentPhysical;
    if (!pp) return false;
    if (pp.boundary) return true;
    return isInBoundary(pp);
};

export class Phys<T extends Physical = Physical> implements Iterable<T> {

    public static from(iterable: Iterable<Component>) {
        return new Phys(
            Array
                .from(iterable)
                .reduce((t, x) => x.is(Physical) ? t.concat([x.as(Physical)]) : t, [] as Physical[])
        );
    }

    private constructor(private list: T[]) {
    }

    public size() {
        return this.list.length;
    }

    public bounds() {
        return Rect.fromMany(this.list.filter(x => x.alive).map(x => x.bounds(RelativeTo.Model)));
    }

    public polarize<U extends Physical>(ct: ComponentType<U>): Phys<U>;
    public polarize<U extends Physical>(c: Convention<U>): Phys<U>;
    public polarize(toc: any) {
        const ts = [] as any[];
        for (const c of this.list) {
            if (c.is(toc)) {
                ts.push(c);
            }
        }
        return Phys.from(ts);
    }

    public first() {
        return this.list[0] ?? null;
    }

    public last() {
        return this.list[this.list.length - 1] ?? null;
    }

    public highest() {
        return this.orderBackToFront().last();
    }

    public largest() {
        return this.list.reduce((t, x) => t.size().area() > x.size().area() ? t : x);
    }

    public smallest() {
        return this.list.reduce((t, x) => t.size().area() < x.size().area() ? t : x);
    }

    public uniques() {
        return new Phys(Array.from(new Set(this.list)));
    }

    public whereIn(physicals: Iterable<Physical>) {
        const set = new Set(physicals);
        return new Phys(this.list.filter(x => set.has(x)));
    }

    public whereNotIn(physicals: Iterable<Physical>) {
        const set = new Set(physicals);
        return new Phys(this.list.filter(x => !set.has(x)));
    }

    public whereAbove(target: Physical) {
        return new Phys(this.list.filter(x => x.isAbove(target)));
    }

    public whereBehind(target: Physical) {
        return new Phys(this.list.filter(x => x.isBehind(target)));
    }

    public whereIsAncestorOf(target: Physical) {
        return new Phys(this.list.filter(x => x.isAncestorOf(target)));
    }

    public whereIsDescendantOf(target: Physical) {
        return new Phys(this.list.filter(x => x.isDescendantOf(target)));
    }

    public whereNotAncestorOf(target: Physical) {
        return new Phys(this.list.filter(x => !x.isAncestorOf(target)));
    }

    public whereNotDescendantOf(target: Physical) {
        return new Phys(this.list.filter(x => !x.isDescendantOf(target)));
    }

    public whereBoundsContain(target: Physical) {
        return new Phys(this.list.filter(x => x.bounds(RelativeTo.Model).contains(target.bounds(RelativeTo.Model))));
    }

    public whereContainedIn(rect: Rect) {
        return new Phys(this.list.filter(x => rect.contains(x.bounds(RelativeTo.Model))));
    }

    public includes(target: T) {
        return this.list.includes(target);
    }

    public find(predicate: (x: T, i: number) => boolean) {
        return this.list.find(predicate);
    }

    public forEach(callback: (x: T, i: number) => void) {
        this.list.forEach(callback);
        return this;
    }

    public filter(predicate: (x: T, i: number) => boolean) {
        return new Phys(this.list.filter(predicate));
    }

    public map<U>(selector: (x: T, i: number) => U): U[] {
        return this.list.map(selector);
    }

    public flatMap<U>(selector: (x: T, i: number) => U[]): U[] {
        return this.list.flatMap(selector);
    }

    public orderBackToFront() {
        return new Phys(this.list.slice().sort(topToBottomComparer));
    }

    public orderFrontToBack() {
        return new Phys(this.list.slice().sort((a, b) => topToBottomComparer(a, b) * -1));
    }

    public takeHeads() {
        const set = this.toSet() as Set<Component>;
        return new Phys(this.list.filter(x => !x.ancestors().find(a => set.has(a))));
    }

    public flatten() {
        return Phys.from(this.list.flatMap(x => x.flatten(true)));
    }

    public bottom() {
        const set = this.toSet() as Set<Component>;
        for (const p of this.list) {
            for (const a of p.ancestors()) {
                set.delete(a);
            }
        }
        return Array.from(set);
    }

    public nearestTo(target: Physical | Point): T {
        const pt = target instanceof Point ? target : target.center(RelativeTo.Model);

        let nearDist = Number.MAX_SAFE_INTEGER;
        let nearObj = null as T;

        for (const obj of this.list) {
            const dist = obj.center(RelativeTo.Model).distanceTo(pt);
            if (nearDist > dist) {
                nearDist = dist;
                nearObj = obj;
            }
        }

        return nearObj;
    }

    public farthestFrom(target: Physical | Point): T {
        const pt = target instanceof Point ? target : target.center(RelativeTo.Model);

        let farDist = Number.MIN_SAFE_INTEGER;
        let farObj = null as T;

        for (const obj of this.list) {
            const dist = obj.center(RelativeTo.Model).distanceTo(pt);
            if (farDist < dist) {
                farDist = dist;
                farObj = obj;
            }
        }

        return farObj;
    }

    public allSameType(specificType?: string) {
        let type = specificType;
        for (const p of this.list) {
            if (!!type && p.type !== type) return false;
            type = p.type;
        }
        return true;
    }

    public allSameSize() {
        let s = null as Point;
        for (const p of this.list) {
            if (!!s && !s.equals(p.size())) return false;
            s = p.size();
        }
        return true;
    }

    public key(tag: string) {
        return `${tag}(${this.list.map(x => x.id).join(':')})`;
    }

    [Symbol.iterator](): Iterator<T, any, undefined> {
        return this.list[Symbol.iterator]();
    }

    public toArray() {
        return Array.from(this.list);
    }

    public toArrayOfIds() {
        return this.list.map(x => x.id);
    }

    public toSet() {
        return new Set(this.list);
    }
}
